/**
 * Legend — Leyenda flotante abajo-izquierda con la clave de colores del grafo.
 * Colapsable por click; arranca abierta para que el alumno entienda la simbología
 * del caso al primer vistazo, y después la puede plegar.
 */

const PANEL_ID = 'cgt-legend';
const STYLE_ID = 'cgt-legend-styles';
const STORAGE_KEY = 'cgt_legend_collapsed';

interface LegendItem {
  color: string;
  label: string;
  title?: string;
}

const NODE_TYPES: LegendItem[] = [
  { color: '#EF4444', label: 'Vital', title: 'Signo vital (FC, PA, SpO₂…)' },
  { color: '#EC4899', label: 'Síntoma', title: 'Síntoma referido por el paciente' },
  { color: '#8B5CF6', label: 'Lab', title: 'Resultado de laboratorio' },
  { color: '#06B6D4', label: 'Imagen', title: 'Imagenología (ECG, Rx, TAC)' },
  { color: '#F59E0B', label: 'Dx', title: 'Diagnóstico — nodo central' },
  { color: '#10B981', label: 'Tto', title: 'Tratamiento / intervención' },
  { color: '#6B7280', label: 'Historia', title: 'Antecedente clínico' },
];

const RELATIONSHIPS: LegendItem[] = [
  { color: '#ff0000', label: 'causa' },
  { color: '#00ff00', label: 'indica' },
  { color: '#00aaff', label: 'trata' },
  { color: '#ffaa00', label: 'asociación' },
];

export class Legend {
  private root: HTMLElement | null = null;
  private collapsed = localStorage.getItem(STORAGE_KEY) === '1';

  mount(): void {
    this.injectStyles();
    if (this.root) return;

    const root = document.createElement('div');
    root.id = PANEL_ID;
    if (this.collapsed) root.classList.add('cgt-legend-collapsed');

    root.innerHTML = `
      <button type="button" class="cgt-legend-toggle" aria-label="Mostrar leyenda">
        <span class="cgt-legend-toggle-icon">🎨</span>
        <span class="cgt-legend-toggle-text">Leyenda</span>
      </button>
      <div class="cgt-legend-body">
        <div class="cgt-legend-section">
          <h5>Nodos</h5>
          <ul>
            ${NODE_TYPES.map(t => `
              <li title="${t.title ?? ''}">
                <span class="cgt-legend-dot" style="background:${t.color}"></span>
                <span>${t.label}</span>
              </li>
            `).join('')}
          </ul>
        </div>
        <div class="cgt-legend-section">
          <h5>Relaciones</h5>
          <ul class="cgt-legend-rels">
            ${RELATIONSHIPS.map(r => `
              <li>
                <span class="cgt-legend-line" style="background:${r.color}"></span>
                <span>${r.label}</span>
              </li>
            `).join('')}
          </ul>
        </div>
      </div>
    `;

    root.querySelector<HTMLButtonElement>('.cgt-legend-toggle')
      ?.addEventListener('click', () => this.toggle());

    document.body.appendChild(root);
    this.root = root;
  }

  private toggle(): void {
    if (!this.root) return;
    this.collapsed = !this.collapsed;
    this.root.classList.toggle('cgt-legend-collapsed', this.collapsed);
    localStorage.setItem(STORAGE_KEY, this.collapsed ? '1' : '0');
  }

  destroy(): void {
    this.root?.remove();
    this.root = null;
  }

  private injectStyles(): void {
    if (document.getElementById(STYLE_ID)) return;
    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = `
      #${PANEL_ID} {
        position: fixed; bottom: 50px; left: 14px;
        z-index: 700;
        font-family: -apple-system, BlinkMacSystemFont, 'Inter', sans-serif;
        color: #f5f5f7;
        background: rgba(10,10,10,0.82); backdrop-filter: blur(12px);
        border: 0.5px solid rgba(255,255,255,0.1);
        border-radius: 12px;
        overflow: hidden;
        transition: max-width 0.25s ease, padding 0.25s ease;
        max-width: 260px;
      }
      #${PANEL_ID}.cgt-legend-collapsed { max-width: 44px; }
      #${PANEL_ID}.cgt-legend-collapsed .cgt-legend-body { display: none; }
      #${PANEL_ID}.cgt-legend-collapsed .cgt-legend-toggle-text { display: none; }
      #${PANEL_ID} .cgt-legend-toggle {
        width: 100%; display: flex; align-items: center; gap: 8px;
        padding: 10px 12px;
        background: transparent; border: none; cursor: pointer;
        color: #a1a1a6; font-size: 11px; font-weight: 600;
        font-family: inherit;
        border-bottom: 0.5px solid rgba(255,255,255,0.06);
      }
      #${PANEL_ID} .cgt-legend-toggle:hover { color: #fff; }
      #${PANEL_ID}.cgt-legend-collapsed .cgt-legend-toggle {
        border-bottom: none; padding: 11px;
      }
      #${PANEL_ID} .cgt-legend-toggle-icon { font-size: 14px; line-height: 1; }
      #${PANEL_ID} .cgt-legend-body {
        padding: 10px 14px 12px;
        display: flex; flex-direction: column; gap: 10px;
      }
      #${PANEL_ID} .cgt-legend-section h5 {
        font-size: 9px; text-transform: uppercase; letter-spacing: 1px;
        color: #6e6e73; margin: 0 0 6px; font-weight: 600;
      }
      #${PANEL_ID} .cgt-legend-section ul {
        list-style: none; padding: 0; margin: 0;
        display: grid; grid-template-columns: 1fr 1fr; gap: 3px 10px;
      }
      #${PANEL_ID} .cgt-legend-section li {
        display: flex; align-items: center; gap: 6px;
        font-size: 11px; color: #d1d1d6;
      }
      #${PANEL_ID} .cgt-legend-dot {
        width: 9px; height: 9px; border-radius: 50%;
        box-shadow: 0 0 6px currentColor;
        flex-shrink: 0;
      }
      #${PANEL_ID} .cgt-legend-line {
        width: 14px; height: 2px; border-radius: 1px;
        flex-shrink: 0;
      }
      #${PANEL_ID} .cgt-legend-rels { grid-template-columns: 1fr 1fr; }
      @media (max-width: 640px) {
        #${PANEL_ID} { bottom: 80px; }
      }
    `;
    document.head.appendChild(style);
  }
}

export const legend = new Legend();
